import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { usePreferencesStore } from '@/state/preferences';

export interface Location {
  id: string;
  name: string;
  code?: string;
  is_main: boolean;
  /** #870 — per-location carried brands (overrides the dealership-wide list). */
  carried_manufacturers?: string[] | null;
}

export interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  name: string; // Accessor: full name
  full_name: string; // Accessor: full name
  email: string;
  avatar_url?: string;
  role: string;
  /** #701 — derived server-side; drives the default lead-ownership view. */
  is_dealer_admin?: boolean;
  /** D9-08 (#408) — effective permission names derived from this
   * employee's roles (getPermissionsViaRoles(), sorted/unique). Read via
   * useCan() to gate SPA affordances; the server remains the sole
   * enforcement point. */
  permissions?: string[];
  /** #1400 — server-derived: AI Reporting is usable (module on and a
   * non-empty catalog projection through any pack). Read via
   * useCanUseReporting(); the server re-checks every Reporting request. */
  can_use_reporting?: boolean;
  /** #733 — names this employee a lead dispatcher (notified for every
   * ownerless enquiry). See DispatcherResolver. */
  is_lead_dispatcher?: boolean;
  /** #705 — currently in the lead-routing rotation. */
  is_available_for_leads?: boolean;
  /** #705 — out of office until (Y-m-d); back from the start of this day. */
  lead_unavailable_until?: string | null;
  /** #706 — brand specialties (raw employee_specialties rows). */
  specialties?: {
    id: string;
    employee_id: string;
    manufacturer_id: string;
    created_at?: string;
    updated_at?: string;
  }[];
  job_title?: string;
  department?: string;
  phone?: string;
  location_id?: string;
  location?: Location;
  is_active: boolean;
  hire_date?: string;
  bio?: string;
  timezone?: string;
  locale?: string;
  preferences?: Record<string, unknown>;
  employee_number?: string;
  date_of_birth?: string;
  termination_date?: string;
  last_login_at?: string;
  last_login_ip?: string;
  password_changed_at?: string;
  force_password_change?: boolean;
  two_factor_confirmed_at?: string;
}

export interface Tenant {
  id: string;
  name: string;
  domain: string;
}

export interface TenantAuthState {
  employee: Employee | null;
  tenant: Tenant | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface TenantAuthActions {
  login: (employee: Employee, tenant: Tenant) => void;
  logout: () => void;
  setEmployee: (employee: Employee | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setLoading: (isLoading: boolean) => void;
}

export const tenantAuthStore = create<TenantAuthState & TenantAuthActions>()(
  persist(
    (set) => ({
      // State
      employee: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: true,

      // Actions
      login: (employee: Employee, tenant: Tenant) => {
        set({
          employee,
          tenant,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          employee: null,
          tenant: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear persisted state
        localStorage.removeItem('tenant-auth-storage');
        // Clear preferences so the next user doesn't briefly see the previous
        // user's preferences before /auth/me hydrates. setAll({}) also
        // broadcasts to other open tabs so they drop their preferences too.
        localStorage.removeItem('preferences-storage');
        usePreferencesStore.getState().setAll({});
      },

      setEmployee: (employee: Employee | null) => {
        set({ employee });
      },

      setTenant: (tenant: Tenant | null) => {
        set({ tenant });
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading });
      },
    }),
    {
      name: 'tenant-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        employee: state.employee,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Set loading to false after rehydration
        if (state) {
          state.isLoading = false;
        }
      },
    }
  )
);

// Selectors
export const useTenantAuth = () => tenantAuthStore();
export const useEmployee = () => tenantAuthStore((state) => state.employee);
export const useTenant = () => tenantAuthStore((state) => state.tenant);
export const useIsTenantAuthenticated = () => tenantAuthStore((state) => state.isAuthenticated);
